import {
  getCalendarMonthFromFySlot,
  getFinancialYearMonthSequence,
  FY_MONTH_ORDER_SHORT,
} from "../../../utils/financialYear";
import { loadExpenseImportRows } from "./expenseChunksService";
import { isExcludedExpenseImportRow } from "../constants";
import { salesImportBranchName } from "../../salesDataImport/config/salesImportBranchIds";

const MONTH_LABELS = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

export function normalizePartyKey(name) {
  return String(name || "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, " ");
}

export function expenseRowSide(row) {
  const t = String(row.transactionType || "").trim().toUpperCase();
  if (t === "DEBIT" || t === "DR" || t === "D" || t.startsWith("DEBIT") || /\bDR\b/.test(t)) {
    return "debit";
  }
  if (t === "CREDIT" || t === "CR" || t === "C" || t.startsWith("CREDIT") || /\bCR\b/.test(t)) {
    return "credit";
  }
  const amt = Number(row.amount) || 0;
  if (amt < 0) return "credit";
  return "debit";
}

export function expenseRowDebitCredit(row) {
  const amt = Math.abs(Number(row.amount) || 0);
  if (!(amt > 0)) return { debit: 0, credit: 0 };
  return expenseRowSide(row) === "credit" ? { debit: 0, credit: amt } : { debit: amt, credit: 0 };
}

export function isTransferOutRow(row) {
  const vt = String(row.voucherTypeName || "").toUpperCase();
  return vt.includes("TRANSFER") && vt.includes("OUT");
}

export function isTransferInRow(row) {
  const vt = String(row.voucherTypeName || "").toUpperCase();
  if (!vt.includes("TRANSFER")) return false;
  if (vt.includes("OUT")) return false;
  return /\bIN\b/.test(vt) || vt.includes("TRANSFER IN");
}

export function isPaymentVoucherRow(row) {
  const vt = String(row.voucherTypeName || "").toUpperCase();
  return (
    vt.includes("PAYMENT") ||
    vt.includes("RECEIPT") ||
    vt.includes("CONTRA") ||
    vt.includes("BANK PAYMENT") ||
    vt.includes("BANK RECEIPT") ||
    vt.includes("CASH PAYMENT") ||
    vt.includes("CASH RECEIPT")
  );
}

export function isBranchPartyRow(row) {
  const party = String(row.partyName || "").toUpperCase();
  return party.includes("BRANCH") && (party.includes("DEBTOR") || party.includes("CREDITOR") || party.includes("S.DEBTOR"));
}

function rowMatchesBranch(row, branchIdFilter) {
  if (!branchIdFilter || branchIdFilter === "all") return true;
  return String(row.branchId || "").trim() === String(branchIdFilter).trim();
}

export async function loadExpenseRowsForFinancialYear(fyStartYear, onProgress) {
  const seq = getFinancialYearMonthSequence(fyStartYear);
  const all = [];
  for (let i = 0; i < seq.length; i += 1) {
    const { calendarYear, monthIndex, fySlot } = seq[i];
    if (onProgress) onProgress(i + 1, seq.length, calendarYear, monthIndex);
    const rows = await loadExpenseImportRows(calendarYear, monthIndex);
    for (const row of rows) {
      if (isExcludedExpenseImportRow(row)) continue;
      all.push({
        ...row,
        calendarYear,
        monthIndex,
        fySlot,
        fyMonthLabel: FY_MONTH_ORDER_SHORT[fySlot],
        monthLabel: MONTH_LABELS[monthIndex],
      });
    }
  }
  return all;
}

export function listUniqueParties(rows, branchIdFilter = "all") {
  const map = new Map();
  for (const row of rows || []) {
    if (!rowMatchesBranch(row, branchIdFilter)) continue;
    const label = String(row.partyName || "").trim();
    if (!label) continue;
    const key = normalizePartyKey(label);
    if (!map.has(key)) map.set(key, label);
  }
  return [...map.values()].sort((a, b) => a.localeCompare(b, undefined, { sensitivity: "base" }));
}

export function buildPartyMonthlyReport(rows, partyName, branchIdFilter = "all") {
  const key = normalizePartyKey(partyName);
  if (!key) return { months: [], totals: { debit: 0, credit: 0, net: 0 }, vouchers: [] };

  const bySlot = new Map();
  const vouchers = [];

  for (const row of rows || []) {
    if (!rowMatchesBranch(row, branchIdFilter)) continue;
    if (normalizePartyKey(row.partyName) !== key) continue;

    const { debit, credit } = expenseRowDebitCredit(row);
    const slot = Number(row.fySlot) || 0;
    const curr = bySlot.get(slot) || {
      fySlot: slot,
      fyMonthLabel: row.fyMonthLabel || FY_MONTH_ORDER_SHORT[slot] || "",
      monthLabel: row.monthLabel || "",
      calendarYear: row.calendarYear,
      monthIndex: row.monthIndex,
      debit: 0,
      credit: 0,
      net: 0,
      count: 0,
    };
    curr.debit += debit;
    curr.credit += credit;
    curr.net = curr.debit - curr.credit;
    curr.count += 1;
    bySlot.set(slot, curr);

    vouchers.push({
      ...row,
      debit,
      credit,
      branchLabel: salesImportBranchName(row.branchId) || row.branchId || "",
    });
  }

  const months = [...bySlot.values()].sort((a, b) => a.fySlot - b.fySlot);
  const totals = months.reduce(
    (acc, m) => ({
      debit: acc.debit + m.debit,
      credit: acc.credit + m.credit,
      net: acc.net + m.net,
      count: acc.count + m.count,
    }),
    { debit: 0, credit: 0, net: 0, count: 0 }
  );

  vouchers.sort((a, b) => String(a.tranDate).localeCompare(String(b.tranDate)));

  return { months, totals, vouchers };
}

export function buildTransferMonthlyReport(rows, { calendarYear, monthIndex, branchIdFilter = "all" }) {
  const monthRows = (rows || []).filter(
    (r) =>
      r.calendarYear === calendarYear &&
      r.monthIndex === monthIndex &&
      rowMatchesBranch(r, branchIdFilter)
  );

  const transferOut = [];
  const transferIn = [];
  const payments = [];

  for (const row of monthRows) {
    const line = {
      ...row,
      debit: expenseRowDebitCredit(row).debit,
      credit: expenseRowDebitCredit(row).credit,
      branchLabel: salesImportBranchName(row.branchId) || row.branchId || "",
    };
    if (isTransferOutRow(row)) transferOut.push(line);
    else if (isTransferInRow(row)) transferIn.push(line);
    else if (isPaymentVoucherRow(row) || isBranchPartyRow(row)) payments.push(line);
  }

  const sumAmount = (list) => list.reduce((s, r) => s + Math.abs(Number(r.amount) || 0), 0);
  const sumDebit = (list) => list.reduce((s, r) => s + (Number(r.debit) || 0), 0);
  const sumCredit = (list) => list.reduce((s, r) => s + (Number(r.credit) || 0), 0);

  const sortByDate = (a, b) => String(a.tranDate).localeCompare(String(b.tranDate)) || String(a.voucherNo).localeCompare(String(b.voucherNo));
  transferOut.sort(sortByDate);
  transferIn.sort(sortByDate);
  payments.sort(sortByDate);

  return {
    transferOut,
    transferIn,
    payments,
    summary: {
      transferOutCount: transferOut.length,
      transferInCount: transferIn.length,
      paymentsCount: payments.length,
      transferOutAmount: sumAmount(transferOut),
      transferInAmount: sumAmount(transferIn),
      paymentsDebit: sumDebit(payments),
      paymentsCredit: sumCredit(payments),
    },
  };
}

export function branchIdFromAppBranch(branchFilter) {
  if (branchFilter === "Main") return "1";
  if (branchFilter === "Branch 2") return "11";
  if (branchFilter === "Branch 3") return "10";
  return "all";
}

export { MONTH_LABELS, FY_MONTH_ORDER_SHORT, getCalendarMonthFromFySlot };
